import {
  visitAttendees,
  visitCommissions,
  visits,
  visitTasks,
  visitTaskSteps,
} from '../visit'
import { describeTable } from './describeTable'

describeTable(visits, {
  description: `One calendar event for the field team. Customer jobs (keikat) are event_type='visit'; other event types are internal calendar entries (breaks, meetings) and must be excluded from any customer or revenue figure.
Revenue is NOT on this row: it is the sum of visit_tasks (gross, VAT 25.5 % included) plus billable visit_line_items (VAT 0). Use the view analytics.visit_revenue for per-visit revenue instead of re-deriving it.
Which date means what: created_at is the SALE date (myynti) — except for the ~11k visits bulk-imported from Ajas (the old system) in Jan–Feb 2026 (internal_notes contains 'Tuotu ajas.fi' and '- Pvm: '), whose created_at is the import moment; exclude those from any sale-date analysis. coalesce(completed_at, end_time) is the DELIVERY date (liikevaihto). start_time is the calendar slot (used for visit counts and profitability). This ERP has been in real use since about January 2026; visits before that are the Ajas import, with prices restored afterwards from the import notes (about 2 % have none), billed in the old system and without commissions here.
'Real customer job' filter: event_type='visit' AND (property_id IS NOT NULL OR opportunity_id IS NOT NULL OR subscription_id IS NOT NULL OR has visit_tasks) — strips ~50 internal entries per month that are miscategorised as visits.
Channel: created_via='widget' is a customer's own web booking. sales_user_id is the rep who sold/booked it; NULL means no seller — in practice a widget booking or a staff-created visit without a seller (e.g. subscription visits). Seller figures are reliable from the start of 2026. Cancellation status is only tracked from Feb 2026.`,
  columns: {
    status:
      "scheduled = booked and waiting (varattu), in_progress = started (keskeneräinen), completed = delivered (toteutunut — the only figure that is a fact), cancelled, unscheduled = created but no slot yet. 'avoin työ' = scheduled + in_progress.",
    eventType:
      "Only 'visit' rows are customer jobs; every other value is an internal calendar entry.",
    startTime:
      'Planned start of the slot (Europe/Helsinki wall clock is what users think in). NULL for unscheduled visits.',
    endTime:
      'Planned end of the slot. Fallback delivery anchor for work not yet completed.',
    completedAt:
      'When the technician marked the job done. Set on every completed visit; primary delivery (liikevaihto) anchor.',
    startedAt:
      'When the technician started the job. Erased on reschedule, so it is only reliable for live in_progress visits.',
    createdAt:
      'Sale date (myynti) — when the booking was made. Beware the ajas.fi history import (see table description).',
    cancelReason:
      "Free text; 'Työntekijä estynyt' means the technician was unavailable (a staffing event, not lost demand).",
    salesUserId:
      'The rep who sold/booked the visit; NULL = no seller (widget self-service or staff-created without one). Earns the sales commission (1 % inbound / 5–10 % outbound).',
    salesType:
      'inbound (customer contacted us) or outbound (we called them). Almost always inbound.',
    commissionMode:
      'auto = field workers earn their employees.commission_percent share automatically; manual = explicit rows in visit_commissions instead.',
    salesCommissionDisabled:
      'true = the seller earns no automatic sales commission on this visit (split sale etc.); their share is a manual row in visit_commissions instead. salesUserId still names the seller for attribution.',
    createdVia:
      "'widget' when the public booking widget created the row; NULL for staff-created visits.",
    experiment:
      "A/B test variant the booking came from, e.g. 'iv-hinta:b'. NULL when no test was running (most rows). booking_analytics.experiment carries the same value on the searches, so the two give the funnel per variant.",
    internalNotes:
      "Staff notes. Contains 'Tuotu ajas.fi' for legacy-imported visits (see table description for the two import waves).",
    internalAcquisitionData:
      'Last-touch marketing attribution captured at booking (utm, click ids, referrer). See docs/attribution-semantics.md.',
    arrivalWindowStart:
      'Nuohous window bookings: start of the arrival window promised to the customer. NULL on exact-time bookings.',
    arrivalWindowEnd: 'End of the promised arrival window.',
    timeLockedAt:
      'When the planned time became final. NULL with a window set = flexible, route planner may still move it.',
    propertyId:
      'The property the work is done at; join properties → customers for the customer.',
    subscriptionId:
      'Set when the visit belongs to a subscription (huoltosopimus).',
    opportunityId: 'The CRM lead this visit was created from, if any.',
  },
})

describeTable(visitTasks, {
  description: `One service line on a visit (nuohous, IV-puhdistus …), copied from a maintenance_tasks template at booking. This is where revenue lives.
price is GROSS (VAT 25.5 % included): divide by 1.255 for net (liikevaihto, alv 0). Apply discount_percent first: price * (1 - discount_percent/100.0).
billing_type='subscription_included' work brings no new euros (it is covered by the customer's subscription MRR) and counts as 0 in company revenue; 'separately_invoiced' or NULL counts in full.
Filter on the VISIT's status/completed_at, never on visit_tasks.status: technicians rarely tick individual tasks, so ~97 % of tasks on completed visits still read 'pending'.
Service category: join maintenance_tasks on maintenance_task_id and use maintenance_tasks.category (chimney = nuohous, hvac = ilmanvaihto, roof = katto, custom = ad-hoc task). Do not classify by task_name keywords — IV task names contain 'kanavanuohous' and would be misfiled as chimney work.
The priciest task on a visit is the visit's primary service; the whole visit files under it.`,
  columns: {
    price:
      'Gross price in euros, VAT 25.5 % included. Net = price / 1.255. Native from 2026; on Ajas-imported visits restored from the import notes.',
    discountPercent: 'Percentage discount on price (rarely used).',
    billingType:
      'subscription_included = covered by subscription, no new revenue; separately_invoiced = billed to the customer. NULL behaves like separately_invoiced.',
    status:
      'Per-task tick by the technician; mostly left pending even on delivered visits. Do not use for revenue.',
    maintenanceTaskId:
      'Template this line was copied from; carries the service category.',
    taskName:
      'Display name copied from the template (or free text for custom tasks).',
    invoiceId: 'Invoice this task was billed on; NULL while unbilled.',
    billedAt: 'When the task was invoiced.',
    paidAt: 'When the invoice covering this task was paid.',
    metadata: 'Free JSON from the booking flow (fireplace counts etc).',
  },
})

describeTable(visitTaskSteps, {
  description:
    'Checklist steps under a visit task, copied from maintenance_task_steps. Operational detail; rarely useful for business analysis.',
})

describeTable(visitAttendees, {
  description: `Which employees (users) attend a visit. One row per person per calendar day of a multi-day job, so count DISTINCT (visit_id, user_id) for "how many visits did X do". Exclude status in ('declined','cancelled'). Reassignment hard-deletes rows, so created_at is not a stable history.
Join users → employees (employees.user_id) for team and commission percent. The lead (role='lead') owns team attribution; a visit with no lead is attributed to its earliest attendee.`,
  columns: {
    role: 'lead = responsible technician, participant = helper.',
    status:
      'confirmed / pending / declined / cancelled. Declined and cancelled attendees did not do the work.',
    visitEventType:
      'Denormalised copy of visits.event_type used by the double-booking constraint.',
  },
})

describeTable(visitCommissions, {
  description: `Manual provisions on a visit with commission_mode='manual'. A fixed (euro) amount is paid as entered, even on subscription-covered work; a percentage applies to net billable revenue (0 on subscription_included work). Rows on an auto-mode visit pay nothing. See docs/commission-semantics.md §3c.`,
  columns: {
    commissionType:
      'fixed = euro amount, percentage = percent of net billable revenue.',
    commissionValue: 'Euros for fixed, percent for percentage.',
    visitTaskId:
      'When set, the provision is scoped to that one task; NULL = whole visit.',
    userId: 'Who receives the provision.',
  },
})
